import { FC, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Typography } from '@mui/material'

import { useGetDashboardGamesWithoutAnalysisQuery, useGetTopGamesByAppIdsQuery } from '../../../../api/dashboard'
import { filterRowsByName, getAppIdsWithGames, toNotAnalyzedRows } from '../../helpers/dashboardHelpers'
import { DashboardAction, DashboardColumn, DashboardListDialogProps } from '../../types/Dashboard'
import { DashboardGamesDialog } from '../DashboardGamesDialog/DashboardGamesDialog'
import { DashboardGamesTable } from '../DashboardGamesTable/DashboardGamesTable'

const columns = [DashboardColumn.Icon, DashboardColumn.Name, DashboardColumn.Publisher, DashboardColumn.Rank, DashboardColumn.DaysInTop200]
const actions = [DashboardAction.ViewAnalyses, DashboardAction.Edit]

export const NotAnalyzedGamesDialog: FC<DashboardListDialogProps> = ({ open, marketIso, onClose, onAction, storedSubgenres }) => {
  const { t } = useTranslation()
  const [search, setSearch] = useState('')
  useEffect(() => setSearch(''), [open])

  const gamesQuery = useGetDashboardGamesWithoutAnalysisQuery(marketIso, { skip: !open, refetchOnMountOrArgChange: true })
  const appIds = useMemo(() => getAppIdsWithGames(gamesQuery.data ?? []), [gamesQuery.data])
  const ranksQuery = useGetTopGamesByAppIdsQuery({ marketIso, appIds }, { skip: !open || !appIds.length })

  const rows = useMemo(() => toNotAnalyzedRows(gamesQuery.data ?? [], ranksQuery.data ?? {}, marketIso), [gamesQuery.data, marketIso, ranksQuery.data])
  const shownRows = useMemo(() => filterRowsByName(rows, search), [rows, search])

  return (
    <DashboardGamesDialog
      open={open}
      title={t('internal-analysts:dashboard_without_analysis_title')}
      onClose={onClose}
      search={search}
      onSearchChange={setSearch}
      isLoading={gamesQuery.isFetching || ranksQuery.isFetching}
      error={gamesQuery.isError ? t('internal-analysts:dashboard_list_failed') : undefined}
      summary={
        <>
          <Typography>{t('internal-analysts:dashboard_games_count', { count: rows.length })}</Typography>
          {ranksQuery.isError && <Typography color="error.main">{t('internal-analysts:dashboard_ranks_failed')}</Typography>}
        </>
      }
    >
      <DashboardGamesTable
        rows={shownRows}
        columns={columns}
        actions={actions}
        onAction={onAction}
        storedSubgenres={storedSubgenres}
        sortByRank
        noRowsLabel={t('internal-analysts:dashboard_no_games')}
      />
    </DashboardGamesDialog>
  )
}
